/**
 * A catalog entry id — `source[:sourceType][:sourceDocumentType]`, as the API
 * builds it. The hierarchy refers to entries by this.
 */
export type KpiSourceId = string;

/** Chips the admin list shows on an entry, as the API tags them. */
export type KpiSourceTag =
  'Foresight Collection' | 'Document Hub' | 'Third party' | 'Manual entry' | 'Managed services' | 'AI';

/**
 * One combination of the three provenance columns a KPI source row carries,
 * as the API ships it in `/configuration.kpiSources`. The catalog is listed in
 * the API's default priority order (index 0 wins), with pinned entries last.
 */
export interface KpiSourceCatalogEntry {
  id: KpiSourceId;
  /** The channel — the API's `KpiCustomDataSource` value. */
  source: string;
  /** Who produced the value: `manual`, `import`, `ai_extraction`, or '' when there is no producer. */
  sourceType: string;
  /** The document kind, or null when the combination is not document-specific. */
  sourceDocumentType: string | null;
  label: string;
  description?: string;
  tags: KpiSourceTag[];
  /** Always ranks last and cannot be re-ordered or excluded. */
  pinned?: boolean;
}

export function createKpiSourceCatalogEntry(
  overrides: Partial<KpiSourceCatalogEntry> = {}
): KpiSourceCatalogEntry {
  const source = overrides.source ?? '';
  const sourceType = overrides.sourceType ?? '';
  const sourceDocumentType = overrides.sourceDocumentType ?? null;
  return {
    id: [source, sourceType, sourceDocumentType].filter(Boolean).join(':'),
    source,
    sourceType,
    sourceDocumentType,
    label: '',
    tags: [],
    ...overrides,
  };
}

/**
 * Keep only entries that carry the catalog contract. An API still serving an
 * older `kpiSources` shape (no `id`, no `tags`) then yields an empty catalog,
 * which the page renders as "not available" rather than failing to render.
 */
export function sanitizeKpiSourceCatalog(raw: unknown): KpiSourceCatalogEntry[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.flatMap((candidate): KpiSourceCatalogEntry[] => {
    if (typeof candidate !== 'object' || candidate === null) {
      return [];
    }
    const entry = candidate as Record<string, unknown>;
    if (
      typeof entry.id !== 'string' ||
      typeof entry.source !== 'string' ||
      typeof entry.label !== 'string' ||
      !Array.isArray(entry.tags)
    ) {
      return [];
    }
    // A well-formed entry round-trips unchanged; optional fields are only set when present.
    const sanitized: KpiSourceCatalogEntry = {
      id: entry.id,
      source: entry.source,
      sourceType: typeof entry.sourceType === 'string' ? entry.sourceType : '',
      sourceDocumentType: typeof entry.sourceDocumentType === 'string' ? entry.sourceDocumentType : null,
      label: entry.label,
      tags: entry.tags.filter((tag): tag is KpiSourceTag => typeof tag === 'string'),
    };
    if (typeof entry.description === 'string') {
      sanitized.description = entry.description;
    }
    if (entry.pinned === true) {
      sanitized.pinned = true;
    }
    return [sanitized];
  });
}

/**
 * How a KPI cell picks between its sources — the API's `KpiSourceResolutionStrategy`.
 * `latest` is the API's default for every tenant: the most recently received value
 * wins and the order below is ignored; `hierarchy` ranks by that order.
 */
export const KPI_SOURCE_RESOLUTION_STRATEGIES = ['latest', 'hierarchy'] as const;
export type KpiSourceResolutionStrategy = (typeof KPI_SOURCE_RESOLUTION_STRATEGIES)[number];

export function isKpiSourceResolutionStrategy(value: unknown): value is KpiSourceResolutionStrategy {
  return KPI_SOURCE_RESOLUTION_STRATEGIES.includes(value as KpiSourceResolutionStrategy);
}

export const DEFAULT_KPI_SOURCE_RESOLUTION_STRATEGY: KpiSourceResolutionStrategy = 'latest';

/** A strategy as the API ships it in `/configuration.kpiResolutionStrategies`, default first. */
export interface KpiResolutionStrategyEntry {
  id: KpiSourceResolutionStrategy;
  label: string;
  description: string;
  /** Whether the order of the hierarchy means anything; sources can be excluded under every strategy. */
  orderable: boolean;
}

/**
 * Keep only strategies that carry the contract. An API that predates strategies
 * ships no list, and the page then offers no choice — it behaves as it did.
 */
export function sanitizeKpiResolutionStrategies(raw: unknown): KpiResolutionStrategyEntry[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.flatMap((candidate): KpiResolutionStrategyEntry[] => {
    if (typeof candidate !== 'object' || candidate === null) {
      return [];
    }
    const entry = candidate as Record<string, unknown>;
    if (
      !isKpiSourceResolutionStrategy(entry.id) ||
      typeof entry.label !== 'string' ||
      typeof entry.description !== 'string' ||
      typeof entry.orderable !== 'boolean'
    ) {
      return [];
    }
    return [
      {
        id: entry.id,
        label: entry.label,
        description: entry.description,
        orderable: entry.orderable,
      },
    ];
  });
}

export interface KpiSourceHierarchyEntry {
  id: KpiSourceId;
  /** Excluded entries stay in the list so re-enabling one restores its slot. */
  enabled: boolean;
}

/**
 * Whether a value a person types in the grid is pinned as the cell's value (FD-5414): `ask` prompts
 * on each save, `always` pins silently, `never` never pins. The grid sends the person's answer as the
 * write's own `pin` flag; the API falls back to this setting for writes that carry none.
 */
export const KPI_PIN_USER_EDITS_MODES = ['ask', 'always', 'never'] as const;
export type KpiPinUserEditsMode = (typeof KPI_PIN_USER_EDITS_MODES)[number];
export const DEFAULT_KPI_PIN_USER_EDITS: KpiPinUserEditsMode = 'ask';
export function isKpiPinUserEditsMode(value: unknown): value is KpiPinUserEditsMode {
  return KPI_PIN_USER_EDITS_MODES.includes(value as KpiPinUserEditsMode);
}

export interface KpiSourceHierarchyPreferences {
  strategy: KpiSourceResolutionStrategy;
  /** Priority order under `hierarchy`; under `latest` only `enabled` matters, but the order is kept. */
  sources: KpiSourceHierarchyEntry[];
  pinUserEdits: KpiPinUserEditsMode;
}

/**
 * Values people type into Foresight. Rankable like any other source since FD-5414 (a typed value that
 * ranks below a sync shows while pinned), but never excludable: switching it off would hide what the
 * firm's own people enter.
 */
export const USER_ENTERED_KPI_SOURCE = 'user';

/** The provenance columns a stored KPI value carries, as the API ships them on a resolved or source row. */
export interface KpiSourceProvenance {
  source: string;
  sourceType?: string | null;
  sourceDocumentType?: string | null;
}

/**
 * The catalog entry a row's provenance names: by exact id, else by source alone. When the source has several
 * entries (one per producer) and the row names none of them, the fallback keeps the label but drops the tags:
 * they would claim a producer — "AI", "Managed services" — the row never had.
 */
export function findKpiSourceCatalogEntry(
  catalog: KpiSourceCatalogEntry[],
  row: KpiSourceProvenance
): KpiSourceCatalogEntry | undefined {
  const id = [row.source, row.sourceType, row.sourceDocumentType].filter(Boolean).join(':');
  const exact = catalog.find((entry) => entry.id === id);
  if (exact) {
    return exact;
  }
  const bySource = catalog.filter((entry) => entry.source === row.source);
  if (bySource.length === 0) {
    return undefined;
  }
  return bySource.length === 1 ? bySource[0] : { ...bySource[0], tags: [], description: undefined };
}

/** The document kinds a universal-request row can name, as they read to a person. */
const KPI_SOURCE_DOCUMENT_KIND_LABELS: Record<string, string> = {
  financial: 'Financials',
  board_deck: 'Board deck',
  email_memo: 'Investor update',
  investment_memo: 'Investment memo',
};

/**
 * One line that says where a value came from (FD-5985): the channel and the document kind, never the
 * producer, so the two extractor producers of one document read the same. A channel without a phrase
 * reads as its catalog label — "From <label>" for a third party — or as its raw name when the catalog
 * does not know it.
 */
export function kpiSourcePhrase(catalog: KpiSourceCatalogEntry[], row: KpiSourceProvenance): string {
  const kind = KPI_SOURCE_DOCUMENT_KIND_LABELS[row.sourceDocumentType ?? ''] ?? 'Document';
  switch (row.source) {
    case USER_ENTERED_KPI_SOURCE:
      return 'Entered in Foresight';
    case 'starbreaker-user':
      return 'Entered in Foresight Collection';
    case 'starbreaker-firm-request':
      return 'Entered in a firm request';
    // not in the catalog (no writer produces it today), so it has no label to fall back on
    case 'starbreaker-firm-request-doc':
      return `${kind} attached to a firm request`;
    case 'starbreaker-universal-doc':
      return `${kind} extracted from a request`;
    case 'starbreaker-dochub-financials':
      return 'Financials uploaded in Document Hub';
    case 'starbreaker-dochub-board-deck':
      return 'Board deck uploaded in Document Hub';
    case 'starbreaker-dochub-email-memo':
      return 'Investor update uploaded in Document Hub';
    case 'file-import':
      return 'Imported from a file';
  }
  const entry = findKpiSourceCatalogEntry(catalog, row);
  if (entry?.tags.includes('Third party')) {
    return `From ${entry.label}`;
  }
  return entry?.label ?? row.source;
}

/** What a value's review state shows: provisional until review confirms it. */
export type KpiReviewStatus = 'provisional' | 'confirmed';
export const KPI_REVIEW_STATUS_LABELS: Record<KpiReviewStatus, string> = {
  provisional: 'Provisional',
  confirmed: 'Confirmed',
};
/** A value's review state, and its producer for when no state came with it. */
export type KpiReviewedRow = Pick<KpiSourceProvenance, 'sourceType'> & { reviewState?: string | null };

/**
 * The status a value shows: `confirmed` covers a read review matched (`confirmed`) and one it decided (`reviewed`).
 * With no state, an AI read is provisional and anything else shows nothing; so does a state this does not know.
 */
export function kpiReviewStatus(row: KpiReviewedRow | undefined): KpiReviewStatus | null {
  const reviewState = row?.reviewState;
  if (reviewState == null) {
    return row?.sourceType === 'ai_extraction' ? 'provisional' : null;
  }
  switch (reviewState) {
    case 'provisional':
      return 'provisional';
    case 'confirmed':
    case 'reviewed':
      return 'confirmed';
    default:
      return null;
  }
}

export function isAlwaysEnabledKpiSource(entry: Pick<KpiSourceCatalogEntry, 'source'>): boolean {
  return entry.source === USER_ENTERED_KPI_SOURCE;
}

export function configurableKpiSources(catalog: KpiSourceCatalogEntry[]): KpiSourceCatalogEntry[] {
  return catalog.filter((entry) => !entry.pinned);
}

export function pinnedKpiSources(catalog: KpiSourceCatalogEntry[]): KpiSourceCatalogEntry[] {
  return catalog.filter((entry) => entry.pinned);
}

export function getDefaultKpiSourceHierarchy(
  catalog: KpiSourceCatalogEntry[]
): KpiSourceHierarchyPreferences {
  return normalizeKpiSourceHierarchy(null, catalog);
}

/**
 * Coerce a stored preference into a complete hierarchy over the catalog:
 * unknown and pinned ids are dropped, duplicates keep their first slot, any
 * configurable entry the stored value predates is appended enabled — so a
 * combination added to the catalog later surfaces without a data fix — and
 * the always-enabled entries are enabled whatever the stored value says, in the
 * place the stored order gives them. A stored value with no (or an unknown)
 * strategy is `latest`, and one with no pin mode is `ask`: that is how the API
 * reads them, and the page must show what the API does.
 */
export function normalizeKpiSourceHierarchy(
  stored: Partial<KpiSourceHierarchyPreferences> | null | undefined,
  catalog: KpiSourceCatalogEntry[]
): KpiSourceHierarchyPreferences {
  const strategy = isKpiSourceResolutionStrategy(stored?.strategy)
    ? stored.strategy
    : DEFAULT_KPI_SOURCE_RESOLUTION_STRATEGY;
  const configurable = configurableKpiSources(catalog);
  const byId = new Map(configurable.map((entry) => [entry.id, entry]));
  const seen = new Set<KpiSourceId>();
  const sources: KpiSourceHierarchyEntry[] = [];

  for (const entry of stored?.sources ?? []) {
    if (!byId.has(entry.id) || seen.has(entry.id)) {
      continue;
    }
    seen.add(entry.id);
    sources.push({ id: entry.id, enabled: entry.enabled !== false });
  }

  for (const entry of configurable) {
    if (!seen.has(entry.id)) {
      sources.push({ id: entry.id, enabled: true });
    }
  }

  const pinUserEdits = isKpiPinUserEditsMode(stored?.pinUserEdits)
    ? stored.pinUserEdits
    : DEFAULT_KPI_PIN_USER_EDITS;
  return {
    strategy,
    sources: sources.map((entry) =>
      isAlwaysEnabledKpiSource(byId.get(entry.id)!) ? { ...entry, enabled: true } : entry
    ),
    pinUserEdits,
  };
}
